import { Fragment, useState, useMemo, type FC } from 'react';
import { DateFormatter, groupArrayByFieldname } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseButton from 'components/atoms/baseButton';
import CallItem from 'components/molecules/rpm-overview-table/components/reading-table/comments-cell/call-item';
import MessageItem from 'components/molecules/rpm-overview-table/components/reading-table/comments-cell/message-item';
import ReviewedItem from 'components/molecules/rpm-overview-table/components/reading-table/comments-cell/reviewed-item';

import s from './styles.module.scss';

import type { IActivityComments } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';

const COMMENTS_PER_LOAD = 3;

interface ActivityCommentListProps {
  comments: IActivityComments[];
  patientGuid: string;
  deleteComment?: TDeleteCommentFn;
  isErroneous?: boolean;
}

const ActivityCommentList: FC<ActivityCommentListProps> = ({
  comments,
  patientGuid,
  deleteComment,
  isErroneous,
}) => {
  const initialLoadCount = isErroneous ? 0 : COMMENTS_PER_LOAD;
  const [visibleCount, setVisibleCount] = useState(initialLoadCount);

  const allCommentsWithDates = useMemo(() => {
    const sortedCommentsByDate = [...comments].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      return 0;
    });

    const commentWithFormattedDate = sortedCommentsByDate.map((comment) => ({
      ...comment,
      formattedCreateAt: DateFormatter({
        date: comment.createdAt,
        format: DateFormats['MM/dd/yyyy'],
      }),
    }));

    const groupedComments = groupArrayByFieldname(
      commentWithFormattedDate,
      'formattedCreateAt',
    );

    const result: Array<{
      date: string;
      comment: IActivityComments & { formattedCreateAt: string };
      isDateHeader: boolean;
      dateKey: string;
    }> = [];

    Object.keys(groupedComments).forEach((date) => {
      const commentsOfDate = groupedComments[date];
      commentsOfDate.forEach((comment, index) => {
        result.push({
          date,
          comment,
          isDateHeader: index === 0,
          dateKey: date,
        });
      });
    });

    return result;
  }, [comments]);

  const { hasMoreComments, visibleGroupedByDate, orderedDates } =
    useMemo(() => {
      const reversedComments = [...allCommentsWithDates].reverse();
      const visible = reversedComments.slice(0, visibleCount);
      const hasMore = visibleCount < reversedComments.length;

      const grouped: Record<string, typeof visible> = {};
      visible.forEach((item) => {
        if (!grouped[item.dateKey]) {
          grouped[item.dateKey] = [];
        }
        grouped[item.dateKey].push(item);
      });

      const dates: string[] = [];
      visible.forEach((item) => {
        if (!dates.includes(item.dateKey)) {
          dates.push(item.dateKey);
        }
      });

      return {
        hasMoreComments: hasMore,
        visibleGroupedByDate: grouped,
        orderedDates: dates,
      };
    }, [allCommentsWithDates, visibleCount]);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + COMMENTS_PER_LOAD);
  };

  return (
    <div className={s['comment-list-wrapper']}>
      <ul data-cy="comment-list" className={s['comments-list']}>
        {orderedDates.map((date) => {
          const commentsOfDate = visibleGroupedByDate[date];

          return (
            <Fragment key={date}>
              <li data-cy="comments-day" className={s['comments-day']}>
                <p>{date}</p>
              </li>
              {commentsOfDate.map((item) => {
                const { comment } = item;
                const isTypeText = comment.type === 'text';
                const isMessageNull = comment.message === null;
                const isTypeCall = comment.type === 'call';
                const isMockComment = comment.guid.startsWith('id_');

                return (
                  <Fragment key={comment.guid}>
                    {isTypeText && isMessageNull && (
                      <ReviewedItem comment={comment} />
                    )}
                    {isTypeText && !isMessageNull && (
                      <MessageItem
                        comment={comment}
                        isMockComment={isMockComment}
                        deleteComment={deleteComment}
                      />
                    )}
                    {isTypeCall && (
                      <CallItem comment={comment} patientGuid={patientGuid} />
                    )}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </ul>
      {hasMoreComments && (
        <div className={s['show-older-wrapper']}>
          <BaseButton
            onClick={handleShowMore}
            className={s['show-older-button']}
            label="Show older comments"
          />
        </div>
      )}
    </div>
  );
};

export default ActivityCommentList;
